import { useCallback, useState } from "react";
import type { TelemetryDataPoint, TelemetryNote } from "@/types";
import {
  CHART_CONSTANTS,
  findNoteAtMouse,
  groupNotesByX,
  type ChartMargins,
} from "./chartLayout";

interface NoteDragStart {
  noteId: string;
  initialTimeOffset: number;
  mouseX: number;
  initialMouseX?: number;
  initialMouseY?: number;
  isDragging?: boolean;
}

interface UseNoteDragParams {
  notes: TelemetryNote[];
  data: TelemetryDataPoint[];
  currentTimeDomain: [number, number];
  onNoteUpdate?: (noteId: string, newTimeOffset: number) => void;
}

interface UseNoteDragResult {
  draggingNoteId: string | null;
  noteDragStart: NoteDragStart | null;
  hoveredNoteId: string | null;
  beginNoteDrag: (
    mouseX: number,
    mouseY: number,
    margins: ChartMargins,
    chartWidth: number,
  ) => TelemetryNote | null;
  handleNoteDragMove: (
    mouseX: number,
    mouseY: number,
    buttons: number,
  ) => boolean;
  endNoteDrag: (
    mouseX: number,
    margins: ChartMargins,
    chartWidth: number,
  ) => boolean;
  cancelPending: () => void;
  cancel: () => void;
  updateHover: (
    mouseX: number,
    mouseY: number,
    margins: ChartMargins,
    chartWidth: number,
  ) => void;
  clearHover: () => void;
}

// Note-drag state machine: pressing a note marker arms a potential drag, the
// pointer crossing a 3px threshold commits it, and releasing maps the final
// mouse position back to a time offset (constrained to the data bounds).
export const useNoteDrag = ({
  notes,
  data,
  currentTimeDomain,
  onNoteUpdate,
}: UseNoteDragParams): UseNoteDragResult => {
  const [draggingNoteId, setDraggingNoteId] = useState<string | null>(null);
  const [noteDragStart, setNoteDragStart] = useState<NoteDragStart | null>(
    null,
  );
  const [hoveredNoteId, setHoveredNoteId] = useState<string | null>(null);

  // Hit-test a press against note markers; arms the pending drag and returns
  // the hit note, or null when the press is not on a note.
  const beginNoteDrag = useCallback(
    (
      mouseX: number,
      mouseY: number,
      margins: ChartMargins,
      chartWidth: number,
    ): TelemetryNote | null => {
      if (!notes.length || !onNoteUpdate || data.length === 0) return null;

      const hitNote = findNoteAtMouse(
        notes,
        mouseX,
        mouseY,
        currentTimeDomain,
        margins,
        chartWidth,
        CHART_CONSTANTS.NOTE_HIT_RADIUS,
      );

      if (!hitNote) return null;

      // Set up potential note dragging (but don't start dragging yet)
      setNoteDragStart({
        noteId: hitNote.id,
        initialTimeOffset: hitNote.timeOffset,
        mouseX: mouseX,
        initialMouseX: mouseX, // Store initial position for drag detection
        initialMouseY: mouseY,
        isDragging: false, // Not dragging until mouse moves
      });
      return hitNote;
    },
    [notes, onNoteUpdate, data, currentTimeDomain],
  );

  // Returns true when the event was consumed by the note-drag gesture.
  const handleNoteDragMove = useCallback(
    (mouseX: number, mouseY: number, buttons: number): boolean => {
      if (!(noteDragStart && onNoteUpdate && data.length > 0)) return false;

      // Check if left mouse button is still pressed (buttons bitmask: 1 = left button)
      if ((buttons & 1) === 0) {
        // Left button is not pressed, stop dragging
        setDraggingNoteId(null);
        setNoteDragStart(null);
        return true;
      }

      // Check if we should start dragging (mouse moved enough)
      if (
        !noteDragStart.isDragging &&
        noteDragStart.initialMouseX !== undefined &&
        noteDragStart.initialMouseY !== undefined
      ) {
        const distance = Math.sqrt(
          Math.pow(mouseX - noteDragStart.initialMouseX, 2) +
            Math.pow(mouseY - noteDragStart.initialMouseY, 2),
        );

        if (distance > 3) {
          setDraggingNoteId(noteDragStart.noteId);
          setNoteDragStart({
            ...noteDragStart,
            isDragging: true,
            mouseX: mouseX,
          });
        }
        return true;
      }

      // If we're actively dragging, update position
      if (draggingNoteId && noteDragStart.isDragging) {
        setNoteDragStart({
          ...noteDragStart,
          mouseX: mouseX,
        });
      }
      return true;
    },
    [noteDragStart, onNoteUpdate, data, draggingNoteId],
  );

  // Commit a completed drag: map the final mouse position to a time offset and
  // report it. Returns true when an active drag was completed.
  const endNoteDrag = useCallback(
    (mouseX: number, margins: ChartMargins, chartWidth: number): boolean => {
      if (
        !draggingNoteId ||
        !noteDragStart ||
        !noteDragStart.isDragging ||
        !onNoteUpdate ||
        data.length === 0
      ) {
        return false;
      }

      // Convert final mouse position to time offset
      const normalizedX = (mouseX - margins.left) / chartWidth;
      const newTimeOffset =
        currentTimeDomain[0] +
        normalizedX * (currentTimeDomain[1] - currentTimeDomain[0]);

      // Constrain to data bounds
      const dataMin = Math.min(...data.map((d) => d.timestamp));
      const dataMax = Math.max(...data.map((d) => d.timestamp));
      const constrainedTimeOffset = Math.max(
        dataMin,
        Math.min(dataMax, newTimeOffset),
      );

      // Only update if the time offset actually changed
      if (
        Math.abs(constrainedTimeOffset - noteDragStart.initialTimeOffset) > 0.01
      ) {
        onNoteUpdate(draggingNoteId, constrainedTimeOffset);
      }

      // Clear note dragging state
      setDraggingNoteId(null);
      setNoteDragStart(null);
      return true;
    },
    [draggingNoteId, noteDragStart, onNoteUpdate, data, currentTimeDomain],
  );

  // Release an armed-but-never-committed drag (plain click on a note).
  const cancelPending = useCallback(() => {
    if (noteDragStart && !noteDragStart.isDragging) {
      setDraggingNoteId(null);
      setNoteDragStart(null);
    }
  }, [noteDragStart]);

  const cancel = useCallback(() => {
    setDraggingNoteId(null);
    setNoteDragStart(null);
  }, []);

  // Track which note marker group the pointer is over.
  const updateHover = useCallback(
    (
      mouseX: number,
      mouseY: number,
      margins: ChartMargins,
      chartWidth: number,
    ) => {
      // Check if hovering over note markers first
      let hoverNoteId: string | null = null;

      if (notes.length > 0) {
        const markerY = margins.top - 12;

        // Filter notes that are within the visible time range
        const visibleNotes = notes.filter(
          (note) =>
            note.timeOffset >= currentTimeDomain[0] &&
            note.timeOffset <= currentTimeDomain[1],
        );

        // Group notes that are very close together
        const groupedNotes = groupNotesByX(
          visibleNotes,
          (note) =>
            margins.left +
            ((note.timeOffset - currentTimeDomain[0]) /
              (currentTimeDomain[1] - currentTimeDomain[0])) *
              chartWidth,
        );

        // Check if mouse is over any note marker
        for (const group of groupedNotes) {
          const distanceX = Math.abs(mouseX - group.x);
          const distanceY = Math.abs(mouseY - markerY);

          if (
            distanceX <= CHART_CONSTANTS.NOTE_HIT_RADIUS &&
            distanceY <= CHART_CONSTANTS.NOTE_HIT_RADIUS
          ) {
            hoverNoteId = group.notes[0].id;
            break;
          }
        }
      }

      // Update hover state
      if (hoverNoteId !== hoveredNoteId) {
        setHoveredNoteId(hoverNoteId);
      }
    },
    [notes, currentTimeDomain, hoveredNoteId],
  );

  const clearHover = useCallback(() => {
    setHoveredNoteId(null);
  }, []);

  return {
    draggingNoteId,
    noteDragStart,
    hoveredNoteId,
    beginNoteDrag,
    handleNoteDragMove,
    endNoteDrag,
    cancelPending,
    cancel,
    updateHover,
    clearHover,
  };
};
